import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { listReports, resolveReport, Report, AdminApiError } from '../api/admin-client';
import ConfirmDialog from '../components/ConfirmDialog';

const LIMIT = 50;
const STATUS_TABS = ['all', 'open', 'dismissed', 'actioned'] as const;
type StatusTab = typeof STATUS_TABS[number];

// ─── Badge helpers ────────────────────────────────────────────────────────────

const CATEGORY_BADGE: Record<string, string> = {
  harassment: 'badge-red',
  cheating:   'badge-red',
  exploit:    'badge-amber',
  spam:       'badge-gray',
  other:      'badge-gray',
};

// Severity is derived from category for now (no explicit severity field on Report type yet)
const CATEGORY_SEVERITY_LABEL: Record<string, string> = {
  harassment: 'High',
  cheating:   'High',
  exploit:    'Medium',
  spam:       'Low',
  other:      'Low',
};

function severityBadge(category: string) {
  const sev = CATEGORY_SEVERITY_LABEL[category] ?? 'Low';
  const cls =
    sev === 'High'   ? 'badge-red'   :
    sev === 'Medium' ? 'badge-amber' :
                       'badge-gray';
  return <span className={`badge ${cls}`} style={{ fontSize: 10 }}>{sev}</span>;
}

function statusBadge(status: string) {
  const cls =
    status === 'open'      ? 'badge-blue' :
    status === 'actioned'  ? 'badge-red'  :
                             'badge-gray';
  return <span className={`badge ${cls}`}>{status}</span>;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function trunc(s: string, n = 60): string {
  return s.length > n ? s.slice(0, n) + '…' : s;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ReportQueue() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<StatusTab>('open');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [reports, setReports] = useState<Report[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Resolve dialog
  const [resolveTarget, setResolveTarget] = useState<Report | null>(null);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [resolveError, setResolveError] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async (resetOffset = true) => {
    const nextOffset = resetOffset ? 0 : offset;
    if (resetOffset) {
      setLoading(true);
      setError('');
      setReports([]);
      setOffset(0);
    } else {
      setLoadingMore(true);
    }

    try {
      const status = tab === 'all' ? undefined : tab;
      const result = await listReports({ status, limit: LIMIT, offset: nextOffset });
      const incoming = result.reports ?? [];
      const totalCount = result.total ?? 0;

      if (resetOffset) {
        setReports(incoming);
      } else {
        setReports(prev => [...prev, ...incoming]);
      }

      setTotal(totalCount);
      const loaded = (resetOffset ? 0 : offset) + incoming.length;
      setHasMore(loaded < totalCount);
      if (!resetOffset) setOffset(nextOffset + incoming.length);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load reports');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [tab, offset]);

  // Initial load / tab change
  useEffect(() => {
    load(true);
  }, [tab]);

  // Infinite scroll observer
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !loadingMore && !loading) {
          load(false);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, load]);

  function changeTab(t: StatusTab) {
    setTab(t);
    setOffset(0);
  }

  function openResolve(report: Report) {
    setResolveTarget(report);
    setResolveError('');
    setFieldValues({ action: 'dismiss', reason: '' });
  }

  async function executeResolve() {
    if (!resolveTarget) return;
    const { action, reason } = fieldValues;
    if (!action) return;
    setResolveLoading(true);
    setResolveError('');
    try {
      await resolveReport(resolveTarget.report_id, action, reason ?? '');
      setReports(rs => rs.filter(r => r.report_id !== resolveTarget.report_id));
      setTotal(t => Math.max(0, t - 1));
      setResolveTarget(null);
    } catch (err: any) {
      setResolveError(err?.message ?? 'Resolve failed');
    } finally {
      setResolveLoading(false);
    }
  }

  return (
    <div>
      <div className="page-title">Report Queue</div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
        {STATUS_TABS.map(t => (
          <button
            key={t}
            className={`btn btn-sm ${tab === t ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => changeTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 12, alignSelf: 'center' }}>
          {loading ? 'Loading…' : `${total} total`}
        </span>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Better empty state for open reports */}
      {!loading && reports.length === 0 && !error && tab === 'open' && (
        <div className="empty-state">
          <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
            No open reports — all clear
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            No player reports need attention right now.
          </div>
        </div>
      )}

      {!loading && reports.length === 0 && !error && tab !== 'open' && (
        <div className="empty-state">No reports found for status: {tab}</div>
      )}

      {reports.length > 0 && (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Age</th>
                  <th>Severity</th>
                  <th>Category</th>
                  <th>Reported</th>
                  <th>Reporter</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {reports.map(r => (
                  <tr key={r.report_id}>
                    <td className="text-muted" style={{ whiteSpace: 'nowrap' }}>{timeAgo(r.created_at)}</td>
                    <td>{severityBadge(r.category)}</td>
                    <td>
                      <span className={`badge ${CATEGORY_BADGE[r.category] ?? 'badge-gray'}`}>
                        {r.category}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate(`/accounts?id=${r.reported_account_id}`)}
                        title={r.reported_account_id}
                      >
                        {r.reported_account_id.slice(0, 8)}…
                      </button>
                    </td>
                    <td className="text-mono text-muted" style={{ fontSize: 11 }}>
                      {r.reporter_account_id.slice(0, 8)}…
                    </td>
                    <td style={{ maxWidth: 280 }}>{trunc(r.reason)}</td>
                    <td>{statusBadge(r.status)}</td>
                    <td>
                      {r.status === 'open' && (
                        <button className="btn btn-sm" onClick={() => openResolve(r)}>
                          Resolve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Infinite scroll sentinel */}
          {hasMore && (
            <div ref={sentinelRef} style={{ textAlign: 'center', padding: '16px 0' }}>
              {loadingMore
                ? <span className="spinner" style={{ borderTopColor: 'var(--accent)' }} />
                : <span className="text-muted" style={{ fontSize: 12 }}>Scroll to load more</span>
              }
            </div>
          )}

          {!hasMore && reports.length > 0 && (
            <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 12, color: 'var(--text-muted)' }}>
              Showing all {reports.length} of {total} reports
            </div>
          )}
        </>
      )}

      {/* Resolve dialog */}
      <ConfirmDialog
        open={!!resolveTarget}
        title={`Resolve Report ${resolveTarget?.report_id?.slice(0, 8) ?? ''}`}
        description={resolveTarget ? `Category: ${resolveTarget.category} — ${trunc(resolveTarget.reason, 120)}` : undefined}
        confirmLabel="Resolve"
        variant={fieldValues.action === 'ban' || fieldValues.action === 'suspend' ? 'danger' : 'default'}
        loading={resolveLoading}
        fields={[
          {
            key: 'action',
            label: 'Action',
            type: 'select',
            defaultValue: 'dismiss',
            options: [
              { value: 'dismiss', label: 'Dismiss — no action' },
              { value: 'warn',    label: 'Warn — record warning on account' },
              { value: 'suspend', label: 'Suspend — 24h timeout on reported account' },
              { value: 'ban',     label: 'Ban — permanent ban on reported account' },
            ],
          },
          {
            key: 'reason',
            label: 'Reason / note',
            type: 'textarea',
            placeholder: 'Enter resolution notes...',
          },
        ]}
        fieldValues={fieldValues}
        onFieldChange={(k, v) => setFieldValues(fv => ({ ...fv, [k]: v }))}
        onConfirm={executeResolve}
        onCancel={() => setResolveTarget(null)}
      />

      {resolveError && <div className="error-banner mt-8">{resolveError}</div>}
    </div>
  );
}
